import { computed, ref } from "vue";
import { useGoogleCommon } from "./google-common";
import { useModal } from "@plugins/modal/composables/modal-instance";
import LessonApi from "@apis/editor.lesson.api";
import { userGoogleCredential } from "@plugins/google/user.google.credential";
import { googleAuth } from "@plugins/google/google.auth";
import { PICKER_ERROR } from "@plugins/google/google.constant";
import { isEmpty } from "@utils/var.check";
import { useStore } from "vuex";
import { INTEGRATOR_APP_FEATURE_CATEGORY } from "@modules/app/service/app.constant.ts";
import { useErrorHandler } from "@plugins/common/composables/error.ts";

export function useGoogleDrive() {
  const { showAppAvailabilityWarningModal, showAppAvailabilityErrorModal, openNotLinkedModal } =
    useGoogleCommon();
  const { passiveApiFailHandler } = useErrorHandler();
  const modal = useModal();
  const store = useStore();

  const skipAction = ref(false);

  const hasGoogleDriveAccess = computed(() => {
    return (
      store.getters["app/isIntegratorAppEnabled"](INTEGRATOR_APP_FEATURE_CATEGORY.GOOGLE_DRIVE) &&
      store.getters["app/hasIntegratorAppCategoryAccess"](
        INTEGRATOR_APP_FEATURE_CATEGORY.GOOGLE_DRIVE,
      )
    );
  });

  const checkHasAnyGoogleAttachment = (requestData, googleDocumentRootFolderType) => {
    return new Promise((resolve, reject) => {
      LessonApi.checkHasAnyGoogleAttachment(requestData, googleDocumentRootFolderType).then(
        (response) => {
          if (response.code === "ok") {
            resolve(response);
          }
        },
        (error) => reject(error),
      );
    });
  };

  const checkIfHasGoogleDriveAccess = (showModal, primaryAction, secondaryAction, data) => {
    if (!hasGoogleDriveAccess.value) {
      if (data) {
        checkHasAnyGoogleAttachment(data.requestData, data.googleDocumentRootFolderType)
          .then((response) => {
            if (response.data) {
              showModal().then(
                () => {
                  primaryAction();
                },
                () => {
                  secondaryAction();
                },
              );
            } else {
              primaryAction();
            }
          })
          .catch((error) => {
            passiveApiFailHandler(error);
          });
      } else {
        modal.removeAll();
        showModal().then(
          () => {
            primaryAction();
          },
          () => {
            secondaryAction();
          },
        );
      }
    } else {
      primaryAction();
    }
  };

  const checkUserLinked = async () => {
    await userGoogleCredential.fetchLinkedAccountCredential();
    const userCredentialData = userGoogleCredential.getCredential();

    if (isEmpty(userCredentialData.userLinkedEmail)) {
      return Promise.reject(PICKER_ERROR.EMPTY_EMAIL);
    }

    try {
      if (userCredentialData.isAccessTokenExpired && !isEmpty(userGoogleCredential.accessToken)) {
        await userGoogleCredential.refreshLinkedAccountCredential();
      }
    } catch (error) {
      return Promise.reject(error);
    }

    if (isEmpty(googleAuth.currentUser)) {
      await googleAuth.checkAuthInstanceInitState();
    } else {
      await googleAuth.isEmailSync();
    }

    if (googleAuth.requireSignIn) {
      googleAuth.handleRequestCode();
      skipAction.value = true;
    }

    return Promise.resolve(true);
  };

  return {
    skipAction,
    hasGoogleDriveAccess,
    checkHasAnyGoogleAttachment,
    checkIfHasGoogleDriveAccess,
    checkUserLinked,
    showAppAvailabilityWarningModal,
    showAppAvailabilityErrorModal,
    openNotLinkedModal,
  };
}
